<template>
    <b-form-modal :title="modalTitle" @submit="onSubmit">

        <b-field label="Foto" v-if="!formData.image">
            <b-field class="file" :class="{'has-name': !!selectedFile}">
                <b-upload v-model="selectedFile" class="file-label" accept="image/*" @input="uploadImage">
                    <span class="file-cta">
                        <b-icon class="file-icon" icon="upload"></b-icon>
                        <span class="file-label">Escolher imagem</span>
                    </span>
                    <span class="file-name" v-if="selectedFile">
                        {{ selectedFile.name }}
                    </span>
                </b-upload>
            </b-field>
        </b-field>

        <div class="card card-image" v-if="formData.image && formData.image.id">
            <div class="card-content">
                <div class="title is-4">Imagem do Produto</div>
                <div class="columns is-vcentered is-mobile">
                    <div class="column">
                        <img :src="formData.image.thumbs.small" width="100" height="100" alt="Imagem do Produto">
                    </div>
                    <div class="column is-narrow">
                        <b-button type="is-danger" @click="removeImage(formData.image)">Apagar</b-button>
                    </div>
                </div>
            </div>
        </div>

        <b-field label="Código">
            <b-input v-model="formData.code" required/>
        </b-field>

        <b-field label="Nome">
            <b-input v-model="formData.name" required/>
        </b-field>

        <b-field label="Descrição">
            <b-input type="textarea" v-model="formData.description" :rows="5"/>
        </b-field>

        <hr>
        <div class="title is-3">Preço</div>

        <div class="columns">
            <div class="column is-narrow">
                <b-field label="Unidade de Medida">
                    <b-select v-model="formData.measureUnit" placeholder="Selecione uma unidade">
                        <option value="un">Unidade (un)</option>
                        <option value="kg">Quilo (kg)</option>
                        <option value="g">Grama (g)</option>
                        <option value="l">Litro (l)</option>
                    </b-select>
                </b-field>
            </div>
            <div class="column is-narrow">
                <b-field label="Referência" message="Quantidade de referencia para o cálculo do preço">
                    <b-input type="number" v-model="formData.priceReference" step="1" min="1"/>
                </b-field>
            </div>
            <div class="column">
                <b-field label="Preço" :message="messageHelpPrice">
                    <b-input type="number" v-model="formData.price" min="0.01" step=".01" required/>
                </b-field>
            </div>
        </div>

        <hr>
        <div class="title is-3">Estoque</div>

        <div class="columns is-middle">
            <div class="column">
                <b-field label="Quantidade em estoque" :message="messageHelpStock">
                    <b-input type="number" :step="formData.priceReference" min="0" v-model="formData.stock"/>
                </b-field>
            </div>
            <div class="column is-narrow" v-if="formData.measureUnit === 'g'">
                <div class="is-size-6 has-text-grey">Equivalente a:</div>
                <convertUnit :value="formData.stock" :current-unit="formData.measureUnit"/>
            </div>
        </div>

        <hr>
        <div class="title is-3">Configurações</div>
        <b-checkbox v-model="formData.active">Ativo</b-checkbox>

        <div class="has-text-right" slot="footer">
            <button type="submit" class="button is-primary" :disabled="isUploading">Salvar</button>
        </div>
    </b-form-modal>
</template>

<script>
import {v4 as uuid} from 'uuid';
import db from '@/services/database';
import storage from "@/services/storage";
import store from "@/store";
import convertUnit from "./convertUnit";
import {generateProductThumbs, removeProductImages} from '../produts.actions';

export default {
    name: "editProduct",

    components: {convertUnit},

    props: {
        action: {
            type: String,
            default: 'create'
        },
        id: {
            default: null
        },
        setData: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            selectedFile: null,
            isUploading: false,
            formData: {
                code: null,
                image: null,
                name: null,
                description: null,
                price: 0,
                priceReference: 1,
                measureUnit: 'un',
                stock: 0,
                active: true
            }
        }
    },

    computed: {

        modalTitle() {

            const action = this.action;

            if (action === 'edit') {
                return 'Editar produto';
            }

            if (action === 'duplicate') {
                return 'Duplicar produto';
            }

            return 'Adicionar produto';

        },

        messageHelpStock() {
            const measureUnit = this.formData.measureUnit;
            const stock = this.formData.stock;
            return `A quantidade em estoque está relacionada a unidade de medida (${measureUnit}), ou seja, existe(m) ${stock} ${measureUnit} em estoque`;
        },

        messageHelpPrice() {
            const measureUnit = this.formData.measureUnit;
            const priceReference = this.formData.priceReference;
            const price = parseFloat(this.formData.price).numberFormat(2, ',', '.');
            return `O preço do produto está relacionado a unidade de medida ou seja ${priceReference} ${measureUnit}, custa R$ ${price}`;
        },
    },

    watch: {
        setData(data) {
            this.updateFormData(data);
        }
    },

    methods: {

        async removeImage(image) {
            const loading = this.$buefy.loading.open({});
            await removeProductImages(image.id, image.path, image.thumbs);

            this.selectedFile = null;
            this.formData.image = null;
            
            loading.close();
        },

        /**
         * Enviar um arquivo
         * @param {File} file
         * @return {Promise<void>}
         */
        async uploadImage(file) {

            this.isUploading = true;
            const loading = this.$buefy.loading.open({});

            try {

                const extension = file.name.split('.').pop();
                const id = uuid();
                const path = `${id}/original.${extension}`;
                const storageRef = storage.ref('products');
                const fileRef = storageRef.child(path);

                const result = await fileRef.put(file);
                const url = await result.ref.getDownloadURL();

                const thumbs = await generateProductThumbs(id, extension, file.type);
                
                this.formData.image = {
                    id,
                    path,
                    url,
                    thumbs
                }

            } catch (e) {
                console.error(e);
                this.$buefy.toast.open({
                    type: "is-danger",
                    message: "Ocorreu um erro ao enviar a imagem. Tente novamente mais tarde"
                });
            }

            this.selectedFile = null;
            this.isUploading = false;
            loading.close();

        },

        updateFormData(newData) {
            const formData = {};
            for (const formDataKey in this.formData) {
                const newDataValue = newData[formDataKey];
                formData[formDataKey] = newDataValue !== undefined ? newDataValue : null;
            }

            this.formData = formData;
        },

        async loadData() {
            const id = this.id;
            if (id) {
                const loading = this.$buefy.loading.open();

                const document = await db.collection('products').doc(id).get();
                this.updateFormData(document.data());

                loading.close();
            }
        },

        async onSubmit() {

            const formData = {
                updatedAt: Date.now(),
                userId: store.state.user.uid,
                code: this.formData.code,
                image: this.formData.image,
                name: this.formData.name,
                description: this.formData.description,
                price: parseFloat(this.formData.price),
                priceReference: parseInt(this.formData.priceReference),
                measureUnit: this.formData.measureUnit,
                stock: parseInt(this.formData.stock),
                active: this.formData.active
            };

            const loading = this.$buefy.loading.open();

            if (this.action === 'edit' && this.id) {
                await db.collection('products').doc(this.id).set(formData, {merge: true});
                this.$emit('onSave', {id: this.id, data: formData});
            } else {
                formData.createAt = Date.now();
                const response = await db.collection('products').add(formData);
                this.$emit('onSave', {id: response.id, data: formData});
            }

            loading.close();
        }
    },

    created() {
        if (this.id) {
            this.loadData();
        }

        if (this.setData) {
            this.updateFormData(this.setData);
        }
    }
};
</script>

<style lang="scss">
.card-image {
    margin-bottom: 2rem;
}
</style>